import { BreakpointObserver } from '@angular/cdk/layout';
import { inject, DestroyRef, signal, ElementRef, computed } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Directionality } from '@angular/cdk/bidi';
import { DOCUMENT } from '@angular/common';
import { bindAttribute } from '@bynary/composables/attribute';

/**
 * Creates a signal that tracks whether the given breakpoint matches.
 * Uses the {@link BreakpointObserver} under the hood.
 *
 * @see https://material.angular.io/cdk/layout/overview#breakpoint-observer
 *
 * @example
 * ```ts
 * const isSmallScreen = useBreakpoint('(max-width: 599px)');
 * ```
 *
 * @example
 * You can also use the predefined breakpoints from {@link Breakpoints}:
 * ```ts
 * const isHandset = useBreakpoint(Breakpoints.Handset);
 * ``
 *
 * @param breakpoint - The breakpoint to match
 * @returns A signal holding whether the given breakpoint matches
 */
const useBreakpoint = (breakpoint) => {
    const breakpointObserver = inject(BreakpointObserver);
    return toSignal(breakpointObserver.observe(breakpoint).pipe(map((v) => v.matches)), {
        initialValue: breakpointObserver.isMatched(breakpoint)
    });
};

/**
 * @internal
 *
 * Creates a signal that listens for events on the given target and holds the last corresponding event.
 * By default, the target is the {@link ElementRef#nativeElement native element} of the current component.
 *
 * ATTENTION: Be careful when using this composable. Events are usually not meant to be represented as signals, but there are special
 * use cases, where it makes sense to do so. For example, when you want to track the last event of a certain type, like whether an element
 * is hovered, a breakpoint is matched, or a certain color-scheme used.
 *
 * @example
 * ```ts
 * const mouseMove = useEvent('mousemove');
 * ```
 *
 * @example
 * You can also pass a custom target to listen on:
 * ```ts
 * const mouseEnter = useEvent('mouseenter', myChildElement);
 * ```
 *
 * @param eventName - The name of the event
 * @param target - The target to listen events on
 * @param options - A set of options for adding the event listener (as used in {@link EventTarget#addEventListener})
 *
 * @returns A signal holding the last detected event of the given type
 */
const θuseEvent = (eventName, target = inject(ElementRef).nativeElement, options) => {
    const destroyRef = inject(DestroyRef, { optional: true });
    const events = signal(undefined);
    const listener = (event) => {
        events.set(event);
    };
    target.addEventListener(eventName, listener, options);
    if (destroyRef) {
        destroyRef.onDestroy(() => target.removeEventListener(eventName, listener));
    }
    return events;
};

/**
 * Returns a signal that tracks whether the given media query matches.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 *
 * @example
 * ```ts
 * const isPortrait = useMediaQuery('(orientation: portrait)');
 * ```
 *
 * @param query - The media query to match
 * @returns A signal holding whether the given media query matches
 */
const useMediaQuery = (query) => {
    const mediaQueryList = window.matchMedia(query);
    const event = θuseEvent('change', mediaQueryList);
    return computed(() => {
        const currentEvent = event();
        return currentEvent == null ? mediaQueryList.matches : currentEvent?.matches;
    });
};

/**
 * @internal
 * Normalizes the options for the {@link useColorScheme} composable
 *
 * @param options - A set of {@link IUseColorSchemeOptions options}
 * @returns The normalized options
 */
const normalizeUseColorSchemeOptions = (options) => ({
    store: options?.store ?? signal(null),
    defaultValue: options?.defaultValue
});
/**
 * Returns the preferred color scheme of the browser.
 * The possible values are
 * - 'light', if the user has set the preferred color scheme to 'light' in the browser settings
 * - 'dark', if the user has set the preferred color scheme to 'dark' in the browser settings
 * - null, if the user has not set a preferred color scheme in the browser settings
 *
 * @example
 * ```ts
 * // e.g. if the user has set the preferred color scheme to 'dark' in the browser settings
 * const preferred = usePreferredColorScheme(); // preferred() === 'dark';
 *
 * // If the preferred color scheme changes, the signal will be updated. E.g. if the user changes the preferred color scheme to 'light'
 * preferred(); // 'light'
 * ```
 *
 * @returns A signal holding the preferred color scheme of the browser
 */
const usePreferredColorScheme = () => {
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
    const prefersLight = useMediaQuery('(prefers-color-scheme: light)');
    return computed(() => {
        if (prefersDark()) {
            return 'dark';
        }
        else if (prefersLight()) {
            return 'light';
        }
        return null;
    });
};
/**
 * A composable to handle the used color scheme of the app
 *
 * @example
 * ```ts
 * // Assuming the user has set the preferred color scheme to 'dark' in the browser settings
 * const { preferred, store, resolved } = useColorScheme();
 * preferred(); // 'dark'
 * store(); // 'dark'
 * resolved(); // 'dark'
 *
 * // If the user changes the preferred color scheme to 'light' in the browser settings
 * preferred(); // 'light'
 * store(); // 'dark'
 * resolved(); // 'dark'
 *
 * // If the user changes the color scheme to 'light' in the app
 * store('light');
 * preferred(); // 'light'
 * store(); // 'light'
 * resolved(); // 'light' <- will default to preferred
 *
 * // If the user changes the color scheme to `null` in the app
 * store(null);
 * preferred(); // 'light'
 * store(); // null
 * resolved(); // 'light' <- will default to preferred
 * ```
 *
 * @param options - Options for the composable
 * @returns A set of signals to handle the color scheme
 * - preferred: The preferred color scheme of the user based on the browser settings
 * - store: A signal to override the preferred color scheme
 * - resolved: The resolved color scheme, based on the preferred color scheme and the override. The override will be used if defined, otherwise the preferred color scheme.
 */
const useColorScheme = (options) => {
    const preferred = usePreferredColorScheme();
    const { store, defaultValue } = normalizeUseColorSchemeOptions(options);
    if (!store()) {
        store.set(preferred());
    }
    const resolved = computed(() => store() ?? preferred() ?? defaultValue ?? null);
    return {
        preferred,
        store,
        resolved
    };
};

/**
 * @internal
 * Normalizes the options for the {@link useDirectionality} composable
 *
 * @param options - A set of {@link IUseDirectionalityOptions options}
 * @returns The normalized options
 */
const normalizeOptions = (options) => {
    return {
        target: options?.target ?? inject(DOCUMENT).firstElementChild ?? undefined
    };
};
/**
 * Creates a signal that emits the current directionality
 *
 * @example
 * ```ts
 * const target = document.firstElementChild; // this is the default target and does not need to be specified in the options
 * const direction = useDirectionality(); // direction() === 'ltr' | 'rtl'
 *
 * // If a target is specified, the 'dir' attribute of the target will be updated
 * direction.set('ltr'); // target.getAttribute('dir') === 'ltr'
 *
 * // When the directionality changes, the signal will be updated
 * target.setAttribute('dir', 'rtl'); // direction() === 'rtl'
 * ```
 *
 * @param options - A set of {@link IUseDirectionalityOptions options}
 * @returns A signal holding the current directionality
 */
const useDirectionality = (options) => {
    const normalizedOptions = normalizeOptions(options);
    const directionality = inject(Directionality);
    const destroyRef = inject(DestroyRef);
    const value = signal(directionality.value);
    directionality.change.pipe(takeUntilDestroyed(destroyRef)).subscribe((v) => value.set(v));
    if (normalizedOptions.target) {
        bindAttribute('dir', value, { target: normalizedOptions.target });
    }
    return value;
};

/**
 * @packageDocumentation
 * Composables to observe events and changes
 *
 * @module @bynary/composables/observer
 */

/**
 * Generated bundle index. Do not edit.
 */

export { useBreakpoint, useColorScheme, useDirectionality, useMediaQuery, usePreferredColorScheme };
